"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { CalendarCheck, Compass, FileCheck2, Hammer, ClipboardCheck, ArrowRight } from "lucide-react";
interface StepItem {
  number: string;
  title: string;
  summary: string;
  deliverables: string[];
  icon: React.ComponentType<{
    className?: string;
  }>;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
}
const steps: StepItem[] = [{
  number: "01",
  title: "Initial Consultation & Site Evaluation",
  summary: "We visit your home to review existing floor plans, discuss lifestyle needs, take structural measurements, and define project scopes and realistic budgets.",
  deliverables: ["On-site feasibility review", "Preliminary scope definition", "Initial budget alignment"],
  icon: CalendarCheck,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_step_consultation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_step_consultation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_step_consultation.png-wvc-sizes",
    alt: "Homeowner and project consultant discussing architectural plans at a kitchen island in Brookhaven"
  }
}, {
  number: "02",
  title: "Design, Architecture & Material Selection",
  summary: "Our team develops detailed space layouts, 3D cabinet and lighting renderings, and guides you through selecting tile, fixtures, millwork, and stone slabs.",
  deliverables: ["3D interior layouts", "Curated fixture & finish schedule", "Fixed-price project proposal"],
  icon: Compass,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_step_design_planning.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_step_design_planning.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_step_design_planning.webp-wvc-sizes",
    alt: "Interior design material board with marble samples, brass hardware, and architectural drawings"
  }
}, {
  number: "03",
  title: "Permitting, Engineering & Logistics",
  summary: "Before work begins, we submit engineering plans to the local building authority, secure municipal permits, order long-lead materials, and set protection barriers.",
  deliverables: ["Municipal building permits", "Dust containment setup", "Confirmed trade schedule"],
  icon: FileCheck2,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_step_permitting.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_step_permitting.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_step_permitting.png-wvc-sizes",
    alt: "Residential construction permit drawings and building documents on a work table"
  }
}, {
  number: "04",
  title: "Active Construction & Weekly Updates",
  summary: "Our trades complete framing, mechanicals, insulation, drywall, and finish carpentry with a dedicated project manager delivering weekly progress reports.",
  deliverables: ["Dedicated on-site manager", "Weekly milestone reports", "Clean, orderly job site"],
  icon: Hammer,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_step_construction.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_step_construction.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_step_construction.png-wvc-sizes",
    alt: "Carpenter installing custom white oak kitchen cabinetry during an interior remodel"
  }
}, {
  number: "05",
  title: "Final Walkthrough & Warranty Handover",
  summary: "We walk through every detail room by room to resolve any remaining punch items, complete professional deep cleaning, and deliver all appliance warranties.",
  deliverables: ["Zero-punch-list signoff", "Full manufacturer warranty packet", "Care and maintenance guidelines"],
  icon: ClipboardCheck,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_step_walkthrough.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_step_walkthrough.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_step_walkthrough.png-wvc-sizes",
    alt: "Completed bright contemporary open-concept interior ready for client move-in"
  }
}];
export default function RemodelingProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="remodelingprocess" data-nav="light" ref={ref} className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`max-w-3xl mx-auto text-center mb-16 md:mb-24 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex justify-center mb-4">
            <Badge variant="outline" className="border-primary/40 text-foreground font-mono text-xs uppercase tracking-wider px-3 py-1">
              Methodology
            </Badge>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-foreground leading-[1.15]">
            Our Five-Step Remodeling Process
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every renovation at Brookhaven Elevation Renovations follows a transparent, ordered sequence designed to eliminate surprises, keep budgets steady, and deliver high craftsmanship.
          </p>
        </div>

        {/* 5-Step Process Sequence */}
        <div className="space-y-12 md:space-y-20">
          {steps.map((step, index) => {
          const Icon = step.icon;
          const isReversed = index % 2 === 1;
          return <div key={step.number} data-index={index} className={`transition-all duration-700 delay-[${index * 100}ms] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                <Card className="border border-border/80 shadow-md bg-card overflow-hidden">
                  <div className="grid grid-cols-1 lg:grid-cols-12 items-stretch">
                    {/* Visual Media Column */}
                    <div className={`lg:col-span-5 relative min-h-[260px] sm:min-h-[320px] lg:min-h-full ${isReversed ? "lg:order-2" : "lg:order-1"}`}>
                      <img src={step.image.src} data-wvc-srcset={step.image.srcset} data-wvc-sizes={step.image.sizes} alt={step.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover object-center absolute inset-0" />
                      <div className="absolute top-4 left-4 z-10">
                        <span className="font-mono text-xs font-semibold px-3 py-1 rounded-sm bg-secondary text-secondary-foreground shadow-sm">
                          Stage {step.number}
                        </span>
                      </div>
                    </div>

                    {/* Step Details Column */}
                    <CardContent className={`lg:col-span-7 p-6 sm:p-8 md:p-10 flex flex-col justify-between ${isReversed ? "lg:order-1" : "lg:order-2"}`}>
                      <div>
                        <div className="flex items-center gap-3 mb-4">
                          <div className="size-10 rounded-md bg-accent flex items-center justify-center text-accent-foreground shrink-0 border border-border">
                            <Icon className="size-5" />
                          </div>
                          <span className="text-xs font-mono font-medium text-muted-foreground uppercase tracking-widest">
                            Step {index + 1} of 5
                          </span>
                        </div>

                        <h3 className="font-serif text-2xl sm:text-3xl font-medium text-card-foreground leading-snug">
                          {step.title}
                        </h3>

                        <p className="mt-3 text-muted-foreground text-sm sm:text-base leading-relaxed">
                          {step.summary}
                        </p>

                        <div className="mt-6 pt-6 border-t border-border">
                          <p className="text-xs font-semibold tracking-wide text-foreground uppercase mb-3">
                            Key Deliverables
                          </p>
                          <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                            {step.deliverables.map((item, dIdx) => <li key={dIdx} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                                <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                                <span>{item}</span>
                              </li>)}
                          </ul>
                        </div>
                      </div>

                      <div className="mt-8 flex items-center justify-between pt-4 border-t border-border/60">
                        <span className="text-xs font-mono text-muted-foreground">
                          Brookhaven Project Protocol
                        </span>
                        <Button asChild variant="ghost" size="sm" className="gap-1.5 text-primary hover:text-primary">
                          <Link to="/ContactRequestAnEstimatePage">
                            <span>Inquire for this stage</span>
                            <ArrowRight className="size-3.5" />
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </div>;
        })}
        </div>

        {/* Bottom Call to Action Card */}
        <div className={`mt-16 md:mt-24 p-8 sm:p-10 rounded-xl border border-border bg-accent text-accent-foreground text-center max-w-4xl mx-auto shadow-sm transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h3 className="font-serif text-2xl sm:text-3xl font-medium tracking-tight">
            Ready to Plan Your Interior Renovation?
          </h3>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Schedule an on-site consultation to review your space, discuss design opportunities, and receive an organized preliminary project estimate.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactRequestAnEstimatePage">Schedule Consultation</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="bg-background">
              <Link to="/OurProjectsPage">View Completed Projects</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}